/**
 * 5-Section WBC3-Compliant Familiarisation Items
 * Based on legacy app structure - simpler and cleaner
 * Pre-sailing focused on emergency essentials only
 * SMS procedures integrated into role-specific section
 */

// Section definitions - 5 sections based on legacy app structure
export const FAMILIARISATION_SECTIONS = {
  'pre-sailing': {
    id: 'pre-sailing',
    title: 'Pre-Sailing Familiarisation',
    description: 'Essential safety briefing before vessel departure'
  },
  'emergency-equipment': {
    id: 'emergency-equipment',
    title: 'Emergency Equipment & Procedures',
    description: 'Location and use of all emergency equipment'
  },
  'vessel-systems': {
    id: 'vessel-systems',
    title: 'Vessel Systems & Controls',
    description: 'Critical systems and their operation'
  },
  'task-specific': {
    id: 'task-specific',
    title: 'Task-Specific Familiarisation',
    description: 'Specific operational procedures and equipment'
  },
  'role-specific': {
    id: 'role-specific',
    title: 'Role-Specific Procedures',
    description: 'Position-specific duties, responsibilities and SMS procedures'
  }
};

// Base items that apply to all vessels and all crew
const BASE_ITEMS = {
  'pre-sailing': [
    // EMERGENCY ESSENTIALS ONLY - What everyone needs to know before departure
    {
      id: 'lifejacket_location',
      item: 'Location of lifejackets',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Appendix 8, Section 9'
    },
    {
      id: 'fire_extinguisher_location',
      item: 'Location of fire extinguishers',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Appendix 8, Section 9'
    },
    {
      id: 'muster_points',
      item: 'Muster points and escape routes',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Appendix 8, Section 9'
    },
    {
      id: 'how_to_raise_alarm',
      item: 'How to raise the alarm',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Appendix 8, Section 9'
    },
    {
      id: 'emergency_contacts',
      item: 'Emergency contact information',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 12.2'
    }
  ],
  
  'emergency-equipment': [
    // Comprehensive emergency equipment familiarisation
    {
      id: 'lifejackets_detailed',
      item: 'All lifejacket locations and types available',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 14.11.3'
    },
    {
      id: 'fire_extinguishers_detailed',
      item: 'Location and operation of fire extinguishers',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 16.1.1'
    },
    {
      id: 'fire_extinguisher_types',
      item: 'Types of extinguishers and appropriate use',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 16.1.1'
    },
    {
      id: 'first_aid_location',
      item: 'First aid kit and medical supplies location',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 23.1'
    },
    {
      id: 'mob_equipment',
      item: 'Man overboard equipment locations (lifebuoys, smoke/light)',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 14.11.3'
    },
    {
      id: 'alarm_signals_detailed',
      item: 'All alarm signals and their meanings',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 8.3.1'
    },
    {
      id: 'emergency_exits',
      item: 'All emergency escape routes and exits',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 8.3.1'
    },
    {
      id: 'mob_procedures',
      item: 'Man overboard recovery procedures',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 14.11.3'
    },
    {
      id: 'emergency_lighting',
      item: 'Emergency lighting locations and operation',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 9.7.1'
    },
    {
      id: 'muster_list',
      item: 'Muster list and individual emergency duties',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 8.3.1'
    }
  ],
  
  'vessel-systems': [
    // Critical vessel systems all crew need to understand
    {
      id: 'vessel_layout',
      item: 'General arrangement and layout of vessel',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Appendix 8, Section 7.2'
    },
    {
      id: 'work_areas',
      item: 'Work areas and access routes',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 25.1'
    },
    {
      id: 'emergency_stops_detailed',
      item: 'All emergency stops (fuel, machinery, hydraulics)',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 8.8'
    },
    {
      id: 'fuel_shutoffs',
      item: 'Fuel shut-off valve locations',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 8.10'
    },
    {
      id: 'battery_isolation',
      item: 'Battery isolation switches',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 9.7'
    },
    {
      id: 'bilge_alarms',
      item: 'Bilge alarm system and pumps',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 11.9'
    },
    {
      id: 'navigation_lights',
      item: 'Navigation lights switches',
      roles: ['master', 'crew'],
      role: 'DECK',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 9.5'
    },
    {
      id: 'vhf_radio_operation',
      item: 'VHF radio operation and distress procedures',
      roles: ['master'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 17.1'
    },
    {
      id: 'navigation_equipment',
      item: 'Navigation equipment operation',
      roles: ['master'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 18.1'
    },
    {
      id: 'engine_controls',
      item: 'Main engine controls and indicators',
      roles: ['master', 'engineer'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 8.8'
    },
    {
      id: 'steering_systems',
      item: 'Steering systems and changeover procedures',
      roles: ['master', 'crew'],
      role: 'DECK',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 10.1'
    },
    {
      id: 'weather_limits',
      item: 'Vessel operating limits and weather restrictions',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 12.3'
    }
  ],
  
  'task-specific': [
    // Base operational tasks - conditional items added dynamically
    {
      id: 'mooring_operations',
      item: 'Safe mooring and unmooring procedures',
      roles: ['crew'],
      role: 'DECK',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 25.1'
    },
    {
      id: 'line_handling',
      item: 'Safe line handling and snapback zones',
      roles: ['crew'],
      role: 'DECK',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 25.1'
    },
    {
      id: 'anchor_operations',
      item: 'Anchor deployment and recovery',
      roles: ['crew'],
      role: 'DECK',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 25.1'
    },
    {
      id: 'deck_machinery',
      item: 'Deck machinery operation and safety',
      roles: ['crew'],
      role: 'DECK',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 25.1'
    },
    {
      id: 'winch_operations',
      item: 'Winch and capstan safe operation',
      roles: ['crew'],
      role: 'DECK',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 25.1'
    },
    {
      id: 'lifting_operations',
      item: 'Safe lifting and manual handling procedures',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 25.1'
    },
    {
      id: 'working_aloft',
      item: 'Working at height procedures and equipment',
      roles: ['crew'],
      role: 'DECK',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 25.1'
    },
    {
      id: 'hot_work_awareness',
      item: 'Hot work permit requirements',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.3'
    },
    {
      id: 'deck_hazards',
      item: 'Deck hazards and restricted areas',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 25.1'
    }
  ],
  
  'role-specific': [
    // Master-Specific
    {
      id: 'emergency_command',
      item: 'Emergency response command procedures',
      roles: ['master'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 12.4'
    },
    {
      id: 'hours_rest_recording',
      item: 'Hours of rest regulations and recording',
      roles: ['master'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'MGN 505'
    },
    {
      id: 'drill_requirements',
      item: 'Drill requirements and recording procedures',
      roles: ['master'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 8.3'
    },
    {
      id: 'certification_awareness',
      item: 'Vessel certification status and requirements',
      roles: ['master'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 3'
    },
    {
      id: 'logbook_requirements',
      item: 'Log book entries and legal requirements',
      roles: ['master'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 12.2.7'
    },
    {
      id: 'sms_documentation',
      item: 'SMS documentation requirements and updates',
      roles: ['master'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 12.2.4'
    },
    {
      id: 'port_state_control',
      item: 'Port state control procedures and documentation',
      roles: ['master'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.2'
    },

    // Engineer-Specific
    {
      id: 'engine_start_stop',
      item: 'Main engine start/stop procedures',
      roles: ['engineer'],
      role: 'ENGINEERING',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 8.8'
    },
    {
      id: 'fuel_oil_transfer',
      item: 'Fuel and oil transfer procedures',
      roles: ['engineer'],
      role: 'ENGINEERING',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 8.10'
    },
    {
      id: 'daily_checks',
      item: 'Daily machinery checks and recording',
      roles: ['engineer'],
      role: 'ENGINEERING',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 12.2.7'
    },
    {
      id: 'emergency_power',
      item: 'Emergency power systems and changeover',
      roles: ['engineer'],
      role: 'ENGINEERING',
      classification: 'MANDATORY',
      priority: 'critical',
      wbc3Ref: 'WBC3 Section 9.7'
    },
    {
      id: 'bilge_management',
      item: 'Bilge pumping and oily water regulations',
      roles: ['engineer'],
      role: 'ENGINEERING',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 11.9'
    },
    {
      id: 'spare_parts_location',
      item: 'Critical spare parts locations',
      roles: ['engineer'],
      role: 'ENGINEERING',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.2'
    },

    // Master & Engineer
    {
      id: 'maintenance_procedures',
      item: 'Maintenance log and recording procedures',
      roles: ['engineer', 'master'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.2.7'
    },
    {
      id: 'equipment_manuals',
      item: 'Location of equipment manuals and operating procedures',
      roles: ['master', 'engineer'],
      role: 'MASTER',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.2.4'
    },

    // All Roles - Policies & SMS
    {
      id: 'drug_alcohol_policy',
      item: 'Drug and alcohol policy awareness',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 23.4'
    },
    {
      id: 'environmental_policy',
      item: 'Environmental policy and MARPOL requirements',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 21'
    },
    {
      id: 'security_procedures',
      item: 'Security procedures and threat levels',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 22'
    },
    {
      id: 'fatigue_management',
      item: 'Fatigue management and reporting',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 23.3'
    },

    // SMS & Reporting Procedures (previously in separate section)
    {
      id: 'sms_location',
      item: 'Location of SMS manual and key procedures',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.2.4'
    },
    {
      id: 'risk_assessment_use',
      item: 'Location and use of risk assessments',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.3'
    },
    {
      id: 'permit_to_work',
      item: 'Permit to work system and when required',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.3'
    },
    {
      id: 'defect_reporting',
      item: 'Defect reporting procedures',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 12.2.7'
    },
    {
      id: 'incident_reporting',
      item: 'Incident and near-miss reporting procedures',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'high',
      wbc3Ref: 'WBC3 Section 12.2.7'
    },
    {
      id: 'hazard_observation',
      item: 'Hazard observation cards and reporting',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.3'
    },
    {
      id: 'toolbox_talks',
      item: 'Toolbox talk procedures and participation',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.3'
    },
    {
      id: 'job_safety_analysis',
      item: 'Job safety analysis (JSA) requirements',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.3'
    },
    {
      id: 'emergency_procedures_location',
      item: 'Location of emergency procedures and action cards',
      roles: ['all'],
      role: 'UNIVERSAL',
      classification: 'MANDATORY',
      priority: 'medium',
      wbc3Ref: 'WBC3 Section 12.2.5'
    }
  ]
};

// Conditional items based on vessel configuration
const CONDITIONAL_ITEMS = {
  // Pre-sailing conditional items (emergency essentials only)
  liferaftFitted: {
    condition: (config) => config.hasLiferaft || config.lengthOverall > 12,
    items: {
      'pre-sailing': [
        {
          id: 'liferaft_location_basic',
          item: 'Life raft location and access',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Appendix 8, Section 9'
        }
      ]
    }
  },

  mobRecovery: {
    condition: (config) => config.hasRescueBoat || config.type === 'rib' || config.type === 'pilot',
    items: {
      'pre-sailing': [
        {
          id: 'mob_recovery_basics',
          item: 'Man overboard recovery basics',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Appendix 8, Section 9'
        }
      ]
    }
  },

  pyrotechnics: {
    condition: (config) => ['0', '1', '2'].includes(config.category),
    items: {
      'pre-sailing': [
        {
          id: 'pyrotechnics_location',
          item: 'Pyrotechnics location and purpose',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Appendix 8, Section 9'
        }
      ]
    }
  },

  navigationLights: {
    condition: (config) => config.hasAccommodation || ['0', '1', '2', '3'].includes(config.category),
    items: {
      'pre-sailing': [
        {
          id: 'nav_emergency_lights',
          item: 'Navigation and emergency light switches',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'medium',
          wbc3Ref: 'WBC3 Appendix 8, Section 9'
        }
      ]
    }
  },

  emergencyEngine: {
    condition: (config) => config.hasEmergencySteering || config.type === 'tug' || ['0', '1', '2'].includes(config.category),
    items: {
      'pre-sailing': [
        {
          id: 'emergency_engine_basics',
          item: 'Emergency engine stop locations',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Appendix 8, Section 9'
        }
      ]
    }
  },
  
  // Emergency equipment conditional items
  lengthBased: {
    condition: (config) => config.lengthOverall > 12,
    items: {
      'emergency-equipment': [
        {
          id: 'liferaft_detailed',
          item: 'Liferaft location and deployment method',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 14.11.3'
        },
        {
          id: 'liferaft_hydrostatic',
          item: 'Hydrostatic release operation',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 14.11.3'
        },
        {
          id: 'liferaft_maintenance',
          item: 'Liferaft hydrostatic release inspection',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 14.11.3'
        },
        {
          id: 'liferaft_equipment',
          item: 'Liferaft equipment and survival techniques',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 14.11.3'
        },
        {
          id: 'epirb_activation',
          item: 'EPIRB location and activation procedure',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 14.11.3'
        }
      ]
    }
  },

  sart: {
    condition: (config) => config.hasSART || config.extra_equipment?.includes('sart'),
    items: {
      'emergency-equipment': [
        {
          id: 'sart_operation',
          item: 'SART location and activation',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 14.11.3'
        }
      ]
    }
  },

  immersionSuits: {
    condition: (config) => config.extra_equipment?.includes('immersion_suits'),
    items: {
      'emergency-equipment': [
        {
          id: 'immersion_suits',
          item: 'Immersion suit locations and donning',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 14.11.3'
        }
      ]
    }
  },

  grabBag: {
    condition: (config) => config.equipment?.includes('grab_bag'),
    items: {
      'emergency-equipment': [
        {
          id: 'grab_bag_contents',
          item: 'Grab bag location and contents',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 14.9'
        }
      ]
    }
  },

  categoryBased: {
    condition: (config) => ['0', '1', '2'].includes(config.category),
    items: {
      'emergency-equipment': [
        {
          id: 'pyrotechnic_flares',
          item: 'Flares and pyrotechnic location and expiry checks',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 14.11.3'
        }
      ]
    }
  },
  
  // Vessel systems conditional items
  fixedFireFighting: {
    condition: (config) => config.equipment?.includes('fixed_firefighting'),
    items: {
      'vessel-systems': [
        {
          id: 'fixed_fire_operation',
          item: 'Fixed fire system operation and activation',
          roles: ['master', 'engineer'],
          role: 'MASTER',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 16.1.1'
        },
        {
          id: 'fixed_fire_isolation',
          item: 'System isolation requirements before discharge',
          roles: ['master', 'engineer'],
          role: 'MASTER',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 16.1.1'
        }
      ]
    }
  },

  gasDetection: {
    condition: (config) => config.equipment?.includes('gas_detection'),
    items: {
      'vessel-systems': [
        {
          id: 'gas_alarm_meanings',
          item: 'Gas detection alarm levels and response',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'MGN 659'
        }
      ],
      'task-specific': [
        {
          id: 'gas_testing',
          item: 'Gas testing procedures before entry',
          roles: ['master', 'engineer'],
          role: 'MASTER',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'MGN 659'
        }
      ]
    }
  },

  emergencySteering: {
    condition: (config) => config.hasEmergencySteering,
    items: {
      'vessel-systems': [
        {
          id: 'emergency_steering',
          item: 'Emergency steering changeover procedure',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 10.1'
        }
      ]
    }
  },
  
  // Task-specific conditional items
  winchLifting: {
    condition: (config) => config.equipment?.includes('winch_lifting_gear'),
    items: {
      'task-specific': [
        {
          id: 'crane_limits',
          item: 'Crane load limits and charts location',
          roles: ['crane_operator', 'master'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 13.1'
        },
        {
          id: 'lifting_signals',
          item: 'Standard lifting hand signals',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 13.1'
        },
        {
          id: 'exclusion_zones',
          item: 'Crane exclusion zones and safety',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 13.1'
        },
        {
          id: 'lifting_gear_inspection',
          item: 'Lifting gear inspection requirements',
          roles: ['crane_operator', 'master'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 13.1'
        }
      ]
    }
  },

  towingOperations: {
    condition: (config) => config.operations?.includes('towing_operations'),
    items: {
      'task-specific': [
        {
          id: 'tow_connection',
          item: 'Tow connection and release procedures',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Appendix 7'
        },
        {
          id: 'girting_prevention',
          item: 'Girting dangers and prevention',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Appendix 7'
        },
        {
          id: 'tow_communications',
          item: 'Towing communication protocols',
          roles: ['master'],
          role: 'MASTER',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Appendix 7'
        },
        {
          id: 'towing_risk_assessments',
          item: 'Towing-specific risk assessments location',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 12.3'
        }
      ]
    }
  },

  dangerousGoods: {
    condition: (config) => config.operations?.includes('dangerous_goods'),
    items: {
      'task-specific': [
        {
          id: 'dg_segregation',
          item: 'Dangerous goods segregation requirements',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 20'
        },
        {
          id: 'dg_emergency',
          item: 'DG emergency response procedures',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 20'
        },
        {
          id: 'dg_documentation',
          item: 'DG documentation and manifest requirements',
          roles: ['master'],
          role: 'MASTER',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 20'
        },
        {
          id: 'dg_ppe',
          item: 'PPE requirements for dangerous goods',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 20'
        }
      ]
    }
  },

  diveSupport: {
    condition: (config) => config.operations?.includes('dive_support'),
    items: {
      'task-specific': [
        {
          id: 'dive_emergency',
          item: 'Diver emergency procedures',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Appendix 6'
        },
        {
          id: 'dive_communications',
          item: 'Diver communication systems',
          roles: ['master', 'dive_supervisor'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Appendix 6'
        },
        {
          id: 'dive_exclusion',
          item: 'Diving operation exclusion zones',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Appendix 6'
        }
      ]
    }
  },

  enclosedSpaces: {
    condition: (config) => config.hasEnclosedSpaces,
    items: {
      'task-specific': [
        {
          id: 'enclosed_entry',
          item: 'Enclosed space entry permit procedures',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'MGN 659'
        },
        {
          id: 'rescue_equipment',
          item: 'Enclosed space rescue equipment location',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'MGN 659'
        },
        {
          id: 'enclosed_space_risks',
          item: 'Enclosed space hazards and risks',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'MGN 659'
        }
      ]
    }
  },
  
  // Role-specific conditional items
  passengerOperations: {
    condition: (config) => config.operations?.includes('passenger_operations'),
    items: {
      'role-specific': [
        {
          id: 'passenger_manifest',
          item: 'Passenger manifest procedures',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 12.2.7'
        },
        {
          id: 'passenger_briefing',
          item: 'Passenger safety briefing procedures',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 14.14'
        },
        {
          id: 'passenger_counting',
          item: 'Passenger counting and manifest procedures',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'critical',
          wbc3Ref: 'WBC3 Section 12.2.7'
        },
        {
          id: 'crowd_control',
          item: 'Basic crowd control in emergencies',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 8.3.1'
        },
        {
          id: 'passenger_areas',
          item: 'Passenger areas and crew-only zones',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'medium',
          wbc3Ref: 'WBC3 Section 14.14'
        }
      ]
    }
  },

  galley: {
    condition: (config) => config.hasGalley,
    items: {
      'role-specific': [
        {
          id: 'galley_isolation',
          item: 'Galley power and gas isolation procedures',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 15.1'
        },
        {
          id: 'grease_fire',
          item: 'Grease fire response and fire blanket use',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 16.1'
        },
        {
          id: 'galley_hygiene',
          item: 'Galley hygiene and food safety procedures',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'medium',
          wbc3Ref: 'WBC3 Section 23.1'
        }
      ]
    }
  },

  accommodation: {
    condition: (config) => config.hasAccommodation,
    items: {
      'role-specific': [
        {
          id: 'accommodation_safety',
          item: 'Accommodation emergency exits and procedures',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 15.1'
        },
        {
          id: 'co_detectors',
          item: 'CO detector locations and response',
          roles: ['all'],
          role: 'UNIVERSAL',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 15.1'
        }
      ]
    }
  },

  internationalVoyages: {
    condition: (config) => config.internationalVoyages,
    items: {
      'role-specific': [
        {
          id: 'isps_procedures',
          item: 'ISPS Code procedures and security levels',
          roles: ['master', 'crew'],
          role: 'DECK',
          classification: 'VESSEL_SPECIFIC',
          priority: 'high',
          wbc3Ref: 'WBC3 Section 22'
        },
        {
          id: 'customs_procedures',
          item: 'Customs and immigration procedures',
          roles: ['master'],
          role: 'MASTER',
          classification: 'VESSEL_SPECIFIC',
          priority: 'medium',
          wbc3Ref: 'WBC3 Section 12.2'
        }
      ]
    }
  }
};

/**
 * Get familiarisation items for a specific vessel configuration and crew role
 * @param {Object} vesselConfig - The vessel configuration object
 * @param {string} crewRole - The crew role (master, engineer, crew, all)
 * @returns {Object} Items organized by section
 */
export function getFamiliarisationItems(vesselConfig, crewRole) {
  const itemsBySection = {};
  
  // Initialize sections
  Object.keys(FAMILIARISATION_SECTIONS).forEach(sectionId => {
    itemsBySection[sectionId] = [];
  });
  
  // Add base items
  Object.entries(BASE_ITEMS).forEach(([sectionId, items]) => {
    items.forEach(item => {
      // Check if item applies to this crew role
      if (item.roles.includes('all') || item.roles.includes(crewRole)) {
        itemsBySection[sectionId].push(item);
      }
    });
  });
  
  // Add conditional items
  Object.entries(CONDITIONAL_ITEMS).forEach(([key, conditional]) => {
    // Check if condition is met
    if (conditional.condition(vesselConfig)) {
      Object.entries(conditional.items).forEach(([sectionId, items]) => {
        items.forEach(item => {
          // Check if item applies to this crew role
          if (item.roles.includes('all') || item.roles.includes(crewRole)) {
            // Avoid duplicates
            const exists = itemsBySection[sectionId].some(
              existingItem => existingItem.id === item.id
            );
            if (!exists) {
              itemsBySection[sectionId].push(item);
            }
          }
        });
      });
    }
  });
  
  return itemsBySection;
}

// Export for compatibility
export { BASE_ITEMS, CONDITIONAL_ITEMS };